"use client";

import { useState } from "react";
import { Send, CheckCircle2, Loader } from "lucide-react";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:5001";

export default function ContactForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("loading");
    setErrorMsg("");

    try {
      const res = await fetch(`${API_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || "Something went wrong");
      }

      setStatus("success");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err: unknown) {
      setStatus("error");
      setErrorMsg(err instanceof Error ? err.message : "Failed to submit. Please try again.");
    }
  };

  if (status === "success") {
    return (
      <div className="text-center py-10">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[#00C58E]/10">
          <CheckCircle2 className="h-7 w-7 text-[#00C58E]" />
        </div>
        <h3 className="text-xl font-bold text-[#1F1F1F]">Thank You!</h3>
        <p className="mt-2 text-[#636363]">
          We&apos;ve received your message and will get back to you soon.
        </p>
        <button
          onClick={() => setStatus("idle")}
          className="mt-6 text-sm font-semibold text-[#0056D2] hover:underline"
        >
          Send another message
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-[#1F1F1F]">
            Name *
          </label>
          <input
            id="name"
            type="text"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="Your name"
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-[#1F1F1F] placeholder-gray-400 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 focus:outline-none"
          />
        </div>
        <div>
          <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-[#1F1F1F]">
            Email *
          </label>
          <input
            id="email"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="your@email.com"
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-[#1F1F1F] placeholder-gray-400 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label htmlFor="phone" className="mb-1.5 block text-sm font-medium text-[#1F1F1F]">
          Phone <span className="text-gray-400">(optional)</span>
        </label>
        <input
          id="phone"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          placeholder="+91 98765 43210"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-[#1F1F1F] placeholder-gray-400 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 focus:outline-none"
        />
      </div>

      <div>
        <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-[#1F1F1F]">
          Message *
        </label>
        <textarea
          id="message"
          required
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="How can we help you?"
          className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm text-[#1F1F1F] placeholder-gray-400 focus:border-[#0056D2] focus:ring-2 focus:ring-[#0056D2]/20 focus:outline-none resize-none"
        />
      </div>

      {status === "error" && (
        <p className="text-sm text-red-600">{errorMsg}</p>
      )}

      <button
        type="submit"
        disabled={status === "loading"}
        className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-[#0056D2] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#004BB5] disabled:opacity-60 disabled:cursor-not-allowed sm:w-auto"
      >
        {status === "loading" ? (
          <>
            <Loader className="h-4 w-4 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            Send Message
          </>
        )}
      </button>
    </form>
  );
}
